/**
 * Reminders: a message comes back at a moment the person chose.
 *
 * Server state, one reminder per message, kept by the daemon under
 * `/api/reminders` and shared by every device signed in to the account. This
 * module is the client's copy of that list and the calls that change it. The
 * daemon fires the reminder -- a push to every device -- and this module only
 * ever hears about that as a list refresh, which is the point: a reminder set
 * on the phone shows up here, and one completed here disappears there.
 *
 * # Times are instants, chosen here
 *
 * The daemon stores unix seconds and fires on them; it knows nothing about
 * time zones. "Tomorrow morning" is therefore computed on this device, in its
 * zone, and sent as an instant -- see {@link presets}. Nine o'clock local is
 * the morning; it is a constant rather than a setting until somebody needs it
 * to be one.
 *
 * # Overdue is a state, not an absence
 *
 * A reminder that has fired and has not been completed stays in the list,
 * marked overdue. Swiping the notification away is not dealing with the
 * message, and the list is where a missed reminder is found again. Only an
 * explicit Complete (or a snooze to later) takes it off.
 */

import { computed, signal } from "@preact/signals";
// The transport seam, as in `lib/priority.ts`: the browser build has a cookie,
// the packaged builds have a bearer token, and `request` knows which.
import { request as fetch } from "@transport";

import { authHeaders } from "./settings";

export type ReminderState = "pending" | "overdue" | "done";

export interface Reminder {
  id: number;
  accountId: string;
  emailId: string;
  /** Unix seconds. */
  remindAt: number;
  createdAt: number;
  firedAt: number | null;
  completedAt: number | null;
  state: ReminderState;
  subject: string | null;
  from: { name: string | null; email: string }[];
  /** Unix seconds. */
  receivedAt: number;
}

export class ReminderError extends Error {
  readonly status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = "ReminderError";
    this.status = status;
  }
}

/** Every reminder that is not done, soonest first, as the daemon last said. */
export const items = signal<Reminder[]>([]);
/** How many have fired and not been completed. The badge on the section. */
export const overdue = signal(0);
/** Whether the list has been read at least once this session. */
export const loaded = signal(false);
/** The last refresh's failure, or null. Shown in the section, never as a toast. */
export const problem = signal<string | null>(null);

/** By `accountId:emailId`, for a chip on a message or a row. */
export const byKey = computed(() => {
  const out = new Map<string, Reminder>();
  for (const r of items.value) out.set(keyOf(r.accountId, r.emailId), r);
  return out;
});

export function keyOf(accountId: string, emailId: string): string {
  return `${accountId}:${emailId}`;
}

export function forMessage(accountId: string, emailId: string): Reminder | undefined {
  return byKey.value.get(keyOf(accountId, emailId));
}

// ---- the daemon ----------------------------------------------------------------

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: "same-origin",
    headers: { ...authHeaders(), ...(body === undefined ? {} : { "content-type": "application/json" }) },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const problem = await res.json();
      detail = problem.error ?? problem.detail ?? detail;
    } catch {
      // A proxy error page is HTML; the status line is all there is.
    }
    throw new ReminderError(detail, res.status);
  }
  return (await res.json()) as T;
}

let inflight: Promise<void> | null = null;

/**
 * Re-read the list. Coalesced: the stream can say "something changed" several
 * times a second during a sync, and one read answers them all.
 */
export function refresh(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const page = await request<{ items: Reminder[]; overdue: number }>("GET", "/api/reminders");
      items.value = page.items;
      overdue.value = page.overdue;
      problem.value = null;
    } catch (e) {
      // A daemon from before reminders answers 404 here. That is "none", not
      // a fault to show: the menu item stays and says so when used.
      if (e instanceof ReminderError && e.status === 404) {
        items.value = [];
        overdue.value = 0;
        problem.value = null;
      } else {
        problem.value = e instanceof Error ? e.message : String(e);
      }
    } finally {
      loaded.value = true;
      inflight = null;
    }
  })();
  return inflight;
}

/** Forget everything, on sign-out. */
export function reset(): void {
  items.value = [];
  overdue.value = 0;
  loaded.value = false;
  problem.value = null;
}

function adopt(reminder: Reminder): void {
  const rest = items.value.filter((r) => r.id !== reminder.id);
  if (reminder.state !== "done") rest.push(reminder);
  rest.sort((a, b) => a.remindAt - b.remindAt || a.id - b.id);
  items.value = rest;
  overdue.value = rest.filter((r) => r.state === "overdue").length;
}

function drop(id: number): void {
  const rest = items.value.filter((r) => r.id !== id);
  items.value = rest;
  overdue.value = rest.filter((r) => r.state === "overdue").length;
}

/** Set or move the reminder on a message. */
export async function set(accountId: string, emailId: string, at: Date): Promise<Reminder> {
  const out = await request<{ reminder: Reminder }>(
    "PUT",
    `/api/reminders/${encodeURIComponent(accountId)}/${encodeURIComponent(emailId)}`,
    { remindAt: seconds(at) },
  );
  adopt(out.reminder);
  return out.reminder;
}

export async function remove(id: number): Promise<void> {
  await request<unknown>("DELETE", `/api/reminders/${id}`);
  drop(id);
}

export async function complete(id: number): Promise<void> {
  await request<{ reminder: Reminder }>("POST", `/api/reminders/${id}/complete`);
  drop(id);
}

export async function snooze(id: number, until: Date): Promise<Reminder> {
  const out = await request<{ reminder: Reminder }>("POST", `/api/reminders/${id}/snooze`, {
    until: seconds(until),
  });
  adopt(out.reminder);
  return out.reminder;
}

function seconds(at: Date): number {
  return Math.round(at.getTime() / 1000);
}

// ---- times -----------------------------------------------------------------------

/** The morning, in local time. */
export const MORNING_HOUR = 9;

export interface Preset {
  id: "later" | "tomorrow" | "nextWeek";
  label: string;
  at: Date;
}

/**
 * The three quick answers, computed from `now` in the device's zone.
 *
 * - *In 3 hours*, rounded up to the next five minutes so the time reads
 *   cleanly. Not "later today": at ten in the evening that would be a lie.
 * - *Tomorrow morning*: the next calendar day at {@link MORNING_HOUR}.
 * - *Next week*: the coming Monday at {@link MORNING_HOUR}. On a Monday that is
 *   the one after, because "next week" said on a Monday morning does not mean
 *   in an hour.
 */
export function presets(now: Date = new Date()): Preset[] {
  return [
    { id: "later", label: "In 3 hours", at: inHours(now, 3) },
    { id: "tomorrow", label: "Tomorrow morning", at: morningOf(addDays(now, 1)) },
    { id: "nextWeek", label: "Next week", at: morningOf(nextMonday(now)) },
  ];
}

/** The snooze from a notification: an hour, rounded the same way. */
export function snoozeUntil(now: Date = new Date()): Date {
  return inHours(now, 1);
}

function inHours(now: Date, hours: number): Date {
  const step = 5 * 60 * 1000;
  const raw = now.getTime() + hours * 60 * 60 * 1000;
  return new Date(Math.ceil(raw / step) * step);
}

function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

function morningOf(d: Date): Date {
  const out = new Date(d);
  out.setHours(MORNING_HOUR, 0, 0, 0);
  return out;
}

function nextMonday(now: Date): Date {
  const day = now.getDay(); // 0 Sunday .. 6 Saturday
  const ahead = day === 1 ? 7 : (8 - day) % 7 || 7;
  return addDays(now, ahead);
}

/**
 * When a reminder is, said the way a person would: "Today 15:00", "Tomorrow
 * 09:00", "Wed 09:00" within the week, "3 Oct 09:00" beyond it, with the year
 * only when it is not this one. Overdue ones are prefixed, because the time
 * alone reads as a plan and it is a missed one.
 */
export function describe(reminder: Pick<Reminder, "remindAt" | "state">, now: Date = new Date()): string {
  const at = new Date(reminder.remindAt * 1000);
  const time = at.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  const dayDelta = daysBetween(now, at);
  let day: string;
  if (dayDelta === 0) day = "Today";
  else if (dayDelta === 1) day = "Tomorrow";
  else if (dayDelta === -1) day = "Yesterday";
  else if (dayDelta > 1 && dayDelta < 7) day = at.toLocaleDateString(undefined, { weekday: "short" });
  else
    day = at.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      ...(at.getFullYear() === now.getFullYear() ? {} : { year: "numeric" }),
    });
  const when = `${day} ${time}`;
  return reminder.state === "overdue" ? `Overdue · ${when}` : when;
}

/** Calendar days from `a` to `b` in local time, negative when `b` is earlier. */
function daysBetween(a: Date, b: Date): number {
  const start = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const end = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000));
}

/**
 * A `datetime-local` value for a picker, from a date: local time, to the
 * minute, which is the format the input speaks and `Date` does not produce.
 */
export function inputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
